import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { webhookService, formSchemaService } from '../api';
import { Layout } from '../components/Layout';
import { Button, Input, ThemedText } from '../components';
import { PageHeaderRow } from '../components/PageStyles';
import { ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter } from '../components/ModalStyles';
import { FormGroup, FormLabel, StyledSelect } from '../components/FormStyles';
import { ToggleSwitch, ToggleInput, ToggleSlider } from '../components/ToggleStyles';
import { CodeExampleViewer } from '../components/CodeExampleViewer';
import { formatJson } from '../utils/webhookCodeExamples';

// ─── Styled Components ──────────────────────────────────────────

const PageContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  background: none;
  border: none;
  color: ${props => props.theme.colors.tint};
  cursor: pointer;
  font-size: ${props => props.theme.typography.sizes.default};
  padding: 0;
  &:hover { text-decoration: underline; }
`;

const FormCard = styled.div`
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  box-shadow: ${props => props.theme.shadows.sm || '0 1px 3px rgba(0,0,0,0.08)'};
`;

const SectionTitle = styled.h3`
  font-size: 0.85rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin-top: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const EventRow = styled.div`
  border: 1px solid ${({ theme, $checked }) => $checked ? theme.colors.primary + '40' : theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme, $checked }) => $checked ? theme.colors.primary + '08' : 'transparent'};
  overflow: hidden;
`;

const EventHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  cursor: pointer;
`;

const EventLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const EventActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const EventName = styled.span`
  font-weight: 500;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text};
`;

const FormSchemaPickerRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.md} ${theme.spacing.sm}`};
  font-size: 0.8rem;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.xl};
`;

const SecretDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

const SecretCode = styled.code`
  flex: 1;
  word-break: break-all;
  font-size: 0.85rem;
`;

// ─── Component ──────────────────────────────────────────────────

export const WebhookEndpointPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [eventTypes, setEventTypes] = useState([]);
  const [formSchemas, setFormSchemas] = useState([]);

  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [subscribedEvents, setSubscribedEvents] = useState([]);

  const [expandedEvent, setExpandedEvent] = useState(null);
  const [samplePayloads, setSamplePayloads] = useState({});
  const [sampleFormSchemaId, setSampleFormSchemaId] = useState({});
  const [loadingSample, setLoadingSample] = useState({});
  const [testingEvent, setTestingEvent] = useState(null);

  const [shownSecret, setShownSecret] = useState(null);

  useEffect(() => {
    const init = async () => {
      try {
        const [types, schemas] = await Promise.all([
          webhookService.getEventTypes(),
          formSchemaService.getFormSchemas({ pageSize: 200 }),
        ]);
        setEventTypes(types || []);
        const schemaList = schemas?.items || schemas || [];
        setFormSchemas(Array.isArray(schemaList) ? schemaList : []);

        if (isEdit) {
          const endpoint = await webhookService.getEndpointById(id);
          if (endpoint) {
            setName(endpoint.name || '');
            setUrl(endpoint.url || '');
            setDescription(endpoint.description || '');
            setIsActive(endpoint.isActive);
            setSubscribedEvents(endpoint.subscribedEvents || []);
          } else {
            toast.error('Endpoint not found');
            navigate('/settings?tab=integrations&sub=outbound');
            return;
          }
        }
      } catch (err) {
        toast.error('Failed to load data');
      }
      setLoading(false);
    };
    init();
  }, [id, isEdit, navigate]);

  const toggleEvent = (evt) => {
    setSubscribedEvents(prev =>
      prev.includes(evt) ? prev.filter(e => e !== evt) : [...prev, evt]
    );
  };

  const loadSample = useCallback(async (evt, formSchemaId) => {
    setLoadingSample(prev => ({ ...prev, [evt]: true }));
    try {
      const payload = await webhookService.getSamplePayload(evt, formSchemaId || undefined);
      setSamplePayloads(prev => ({ ...prev, [evt]: payload }));
    } catch {
      toast.error('Failed to load sample payload');
    }
    setLoadingSample(prev => ({ ...prev, [evt]: false }));
  }, []);

  const handleToggleExpand = useCallback((evt) => {
    setExpandedEvent(prev => {
      const next = prev === evt ? null : evt;
      if (next && !samplePayloads[next]) {
        loadSample(next, sampleFormSchemaId[next]);
      }
      return next;
    });
  }, [samplePayloads, sampleFormSchemaId, loadSample]);

  const handleFormSchemaChange = (evt, schemaId) => {
    const parsed = schemaId ? parseInt(schemaId, 10) : null;
    setSampleFormSchemaId(prev => ({ ...prev, [evt]: parsed }));
    loadSample(evt, parsed);
  };

  const handleTestEvent = async (evt) => {
    if (!isEdit) return;
    setTestingEvent(evt);
    try {
      await webhookService.testEndpoint(id, evt, sampleFormSchemaId[evt] || undefined);
      toast.success(`Test ${evt} sent`);
    } catch {
      toast.error(`Test ${evt} failed`);
    }
    setTestingEvent(null);
  };

  const handleSave = async () => {
    if (!name.trim() || !url.trim() || subscribedEvents.length === 0) {
      toast.warning('Name, URL, and at least one event are required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        url: url.trim(),
        description: description.trim() || null,
        subscribedEvents,
        isActive,
      };

      if (isEdit) {
        await webhookService.updateEndpoint(id, payload);
        toast.success('Endpoint updated');
        navigate('/settings?tab=integrations&sub=outbound');
      } else {
        const result = await webhookService.createEndpoint(payload);
        if (result?.secret) {
          setShownSecret(result.secret);
          toast.success('Endpoint created');
        } else {
          toast.success('Endpoint created');
          navigate('/settings?tab=integrations&sub=outbound');
        }
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to save endpoint');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <Layout>
        <PageContainer theme={theme}>
          <p style={{ textAlign: 'center', padding: theme.spacing.xl, color: theme.colors.textSecondary }}>
            Loading...
          </p>
        </PageContainer>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageContainer theme={theme}>
        <PageHeaderRow theme={theme}>
          <div>
            <BackButton theme={theme} onClick={() => navigate('/settings?tab=integrations&sub=outbound')}>
              <ArrowLeft size={14} /> Back to Webhooks
            </BackButton>
            <ThemedText type="title" style={{ marginTop: 8 }}>
              {isEdit ? 'Edit Webhook Endpoint' : 'New Webhook Endpoint'}
            </ThemedText>
          </div>
        </PageHeaderRow>

        <FormCard theme={theme}>
          <SectionTitle theme={theme}>Configuration</SectionTitle>

          <Input
            label="Name"
            required
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="My Webhook Endpoint"
          />

          <Input
            label="URL"
            required
            value={url}
            onChange={e => setUrl(e.target.value)}
            placeholder="https://example.com/webhook"
          />

          <Input
            label="Description"
            type="textarea"
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Optional description of this endpoint"
          />

          {isEdit && (
            <FormGroup theme={theme}>
              <FormLabel theme={theme}>Status</FormLabel>
              <ToggleSwitch theme={theme}>
                <ToggleInput type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
                <ToggleSlider theme={theme} $checked={isActive} />
                <span style={{ fontSize: '0.9rem', color: theme.colors.text }}>{isActive ? 'Active' : 'Inactive'}</span>
              </ToggleSwitch>
            </FormGroup>
          )}

          <SectionTitle theme={theme}>Events</SectionTitle>
          {eventTypes.map(evt => {
            const checked = subscribedEvents.includes(evt);
            const expanded = expandedEvent === evt && checked;
            const isSubmissionEvent = evt === 'SubmissionReceived';

            return (
              <EventRow key={evt} theme={theme} $checked={checked}>
                <EventHeader theme={theme} onClick={() => toggleEvent(evt)}>
                  <EventLeft theme={theme}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleEvent(evt)}
                      onClick={e => e.stopPropagation()}
                    />
                    <EventName theme={theme}>{evt}</EventName>
                  </EventLeft>
                  {checked && (
                    <EventActions theme={theme}>
                      {isEdit && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={e => { e.stopPropagation(); handleTestEvent(evt); }}
                          disabled={testingEvent === evt}
                        >
                          {testingEvent === evt ? 'Sending...' : 'Test'}
                        </Button>
                      )}
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={e => { e.stopPropagation(); handleToggleExpand(evt); }}
                      >
                        {expanded ? 'Hide Payload' : 'Sample Payload'}
                      </Button>
                    </EventActions>
                  )}
                </EventHeader>

                {expanded && (
                  <>
                    {isSubmissionEvent && (
                      <FormSchemaPickerRow theme={theme}>
                        <span>Form Schema:</span>
                        <StyledSelect
                          theme={theme}
                          value={sampleFormSchemaId[evt] || ''}
                          onChange={e => handleFormSchemaChange(evt, e.target.value)}
                          style={{ maxWidth: 250, fontSize: '0.8rem' }}
                        >
                          <option value="">Default (generic)</option>
                          {formSchemas.map(s => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))}
                        </StyledSelect>
                      </FormSchemaPickerRow>
                    )}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: `${theme.spacing.xs} ${theme.spacing.md}`,
                      fontSize: '0.8rem',
                      color: theme.colors.textSecondary,
                    }}>
                      <span>Sample webhook payload</span>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => loadSample(evt, sampleFormSchemaId[evt])}
                        disabled={loadingSample[evt]}
                      >
                        {loadingSample[evt] ? 'Loading...' : 'Refresh'}
                      </Button>
                    </div>
                    <div style={{ padding: `0 ${theme.spacing.md} ${theme.spacing.md}` }}>
                      {loadingSample[evt] ? (
                        <p style={{ textAlign: 'center', padding: theme.spacing.md, color: theme.colors.textSecondary, fontSize: '0.8rem' }}>
                          Loading sample payload...
                        </p>
                      ) : samplePayloads[evt] ? (
                        <CodeExampleViewer
                          snippets={[{ label: 'JSON', language: 'json', code: formatJson(samplePayloads[evt]) }]}
                          defaultTab="JSON"
                        />
                      ) : (
                        <p style={{ textAlign: 'center', padding: theme.spacing.md, color: theme.colors.textSecondary, fontSize: '0.8rem' }}>
                          No sample available
                        </p>
                      )}
                    </div>
                  </>
                )}
              </EventRow>
            );
          })}

          <Footer theme={theme}>
            <Button variant="secondary" onClick={() => navigate('/settings?tab=integrations&sub=outbound')}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : isEdit ? 'Update Endpoint' : 'Create Endpoint'}
            </Button>
          </Footer>
        </FormCard>

        {shownSecret && (
          <ModalOverlay $isOpen theme={theme}>
            <ModalContent theme={theme} style={{ maxWidth: 500 }}>
              <ModalHeader theme={theme}>
                <h3>Webhook Secret</h3>
              </ModalHeader>
              <ModalBody theme={theme}>
                <p style={{ color: theme.colors.warning, fontWeight: 500, marginBottom: theme.spacing.sm }}>
                  Copy this secret now. It will not be shown again.
                </p>
                <SecretDisplay theme={theme}>
                  <SecretCode>{shownSecret}</SecretCode>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => { navigator.clipboard.writeText(shownSecret); toast.success('Copied!'); }}
                  >
                    Copy
                  </Button>
                </SecretDisplay>
                <p style={{ fontSize: '0.8rem', color: theme.colors.textSecondary, marginTop: theme.spacing.sm }}>
                  Use this secret to verify webhook signatures using HMAC-SHA256.
                  The signature is sent in the <code>X-Webhook-Signature</code> header as <code>sha256=&lt;hex&gt;</code>.
                </p>
              </ModalBody>
              <ModalFooter theme={theme}>
                <Button onClick={() => { setShownSecret(null); navigate('/settings?tab=integrations&sub=outbound'); }}>
                  Done
                </Button>
              </ModalFooter>
            </ModalContent>
          </ModalOverlay>
        )}
      </PageContainer>
    </Layout>
  );
};

export default WebhookEndpointPage;
